import React, { useRef, useEffect, useState } from "react"
import { Redirect } from "react-router-dom"
import ReactPlayerLoader from "@brightcove/react-player-loader"
import gsap from "gsap/gsap-core"
import PropTypes from "prop-types"

import {
  years,
  componentSettings,
  totalCycle,
} from "../../services/timelineService"

const sampleData = require("../../fixtures/content.json")
function HeroVideo(props) {
  const [redirectSearch, setRedirectSearch] = useState(false)
  const heroRef = useRef(null)
  let nextCue = 0
  const isMobile = navigator.userAgent.toLowerCase().match(/mobile/i)

  useEffect(() => {
    if (props.bookend) {
      props.heroTl.play()
    }
  })
  /**
   * Intro and close videos use the Brightcove player
   * No subcomponents, no PIP animation - just play, maybe fullscreen?
   */

  /**
   * pause video when tab loses focus:
   * window.addEventListener('blur', () => pauseVideo())
   *
   * play it again when it comes back
   * window.addEventListener('focus', () => playVideo())
   */

  /**
   * Generates supporting content components for hero video to be animated
   * @param {array} cards Array of objects detailing items to animate
   * @return {array} Returns array of React components
   */
  const generateSubcomponents = (cards) => {
    return cards.map((ea, i) => {
      return (
        <div
          // key={`subcomponent-${years[props.currentSegment]}-${props.currentSegment}-${i}-${ea.src}`}
          key={`subcomponent-${years[props.currentSegment]}-${i}`}
          className={`card subcomponent subcomponent-${years[props.currentSegment]}-${i}`}
        >
          <img
            src={`${process.env.PUBLIC_URL}${ea.src}`}
            alt={`${ea.alt} - ${ea.attribution}`}
          />
        </div>
      )
    })
  }

  /**
   * Generate animation cues paired with animation
   * @param {array} cards Array of card objects to animate
   * @return {array} Returns array of objects including cue time and animation function
   */
  const animationCues = (cards, song) => {
    // build animation/timing objects based on json data
    let contentCues = cards.map((ea, i) => {
      return {
        startTime: ea.start,
        animation: () => {
          // hand this in as a prop
          console.log("Animation triggered")
          // props.subComponentTl // add to the tl instead of building it here
          const subComponentTl = gsap.timeline() // add to the tl instead of building it here
          subComponentTl
            .set(`.subcomponent-${years[props.currentSegment]}-${i}`, {
              opacity: 0,
              scale: 1,
              onComplete: () => console.log(`Set: `, i),
            })
            .to(`.subcomponent-${years[props.currentSegment]}-${i}`, {
              scale: 2,
              opacity: 1,
              ease: "power1.in",
              duration: 1,
              onComplete: () => console.log(`Scale: `, i),
            })
            .to(`.subcomponent-${years[props.currentSegment]}-${i}`, {
              scale: 2.25,
              opacity: 0.8,
              ease: "none",
              duration: ea.end ? ea.end - ea.start : 15,
              onComplete: () => console.log(`Hold: `, i),
            })
            .to(`.subcomponent-${years[props.currentSegment]}-${i}`, {
              opacity: 0,
              scale: componentSettings.scale,
              ease: "power1.out",
              duration: 0.4,
              onComplete: () => console.log(`Out: `, i),
            })
            .to(`.subcomponent-${years[props.currentSegment]}-${i}`, {
              opacity: 0,
              scale: 1,
            })
        },
      }
    })
    contentCues.push({
      startTime: song.startTime,
      animation: () => {
        props.setMusicPlaying(true)
        animateVideoBackCenter(".hero-video__container")
      },
    })
    contentCues.push({
      startTime:
        sampleData.years[years[props.currentSegment]].heroVideo.introDuration,
      animation: () => animateVideoPIP(heroRef.current),
    })

    contentCues = contentCues.sort((a, b) =>
      parseInt(a.startTime) < parseInt(b.startTime) ? -1 : 1
    )
    console.log("contentCues", contentCues)

    return contentCues
  }

  /**
   * Set video back to defaults
   * @param {object} identifier class or id string, or Ref object of what to animate
   */
  const cleanUpPlayer = (identifier) => {
    gsap.set(identifier, {
      scale: 0.5,
      opacity: 0,
      pointerEvents: "none",
    })
  }

  /**
   * Create basic player attributes, cue points, and video end actions
   * @param {object} player Player object built by Brightcove library
   */
  const setupPlayer = (player) => {
    player.controls(isMobile)
    player.playsinline(true)
    const playerWidthMult = 0.5
    player.width(window.innerWidth * playerWidthMult)
    player.height(((window.innerWidth * playerWidthMult) / 16) * 9)
    player.qualityMenu({
      defaultResolution: '720p'
    })

    setTimeout(() => {
      if (player.currentTime === 0) {
        player.controls(true)
      }
    }, 1000)

    
    let cue = animationCues(
      sampleData.years[years[props.currentSegment]].cards,
      sampleData.years[years[props.currentSegment]].music
    )

    player.on("timeupdate", () => {
      // make this your check for when to run animations - this runs ~4x/sec.
      // console.log(player.currentTime())

      cue.forEach((ea, i) => {
        if (player.currentTime() === 0) {
          nextCue = 0
        }
        if (player.currentTime() > ea.startTime && nextCue === i) {
          console.log(`Trigger cue ${i}`, ea)
          nextCue++
          console.log("Next Cue #", nextCue)
          return ea.animation()
        }
      })
    })

    player.on("ended", () => {
      gsap.to(heroRef.current, {
        onStart: () => {
          props.handleHeroComplete()
        },
        // scale: 8,
        opacity: 0,
        duration: 1,
        onComplete: () => {
          cleanUpPlayer(".hero-video__container")
          if (props.bookend && props.currentSegment === 0) {
            props.scrubTo("2014")
          } else if (
            props.bookend &&
            props.currentSegment === years.length - 1
          ) {
            setRedirectSearch(true)
          }
        },
      })
    })
  }

  /**
   * Animate the hero video
   * @param {object} tl A timeline object from gsap to add animations to
   * @param {string || object} identifier A class or id string, or a ref object identifying what should be animated
   * @param {object} videoData Imported data about the video to aid in animation, esp. intro and outro durations
   * @param {number} videoDuration Overall duration of the video, from video metadata, to time outro
   * @param {object} player Player object built by Brightcove library
   */
  const animateVideo = (
    tl,
    identifier,
    videoData,
    videoDuration,
    player,
    bookend
  ) => {
    if (bookend) {
      tl.set(identifier, {
        scale: 0.5,
        opacity: 0,
        left: "50%",
        top: "50%",
        pointerEvents: "all",
      }).to(identifier, {
        scale: 1,
        opacity: 1,
        duration: 0.75,
        ease: "power1.inOut",
        onComplete: () => {
          props.handleHeroLoad()
          if (!isMobile) {
            player.play()
          }
        },
      })
    } else {
      tl.set(identifier, {
        scale: 0.5,
        opacity: 0,
        left: "50%",
        top: "50%",
        pointerEvents: "all",
      })
        .to(identifier, {
          scale: 1,
          duration: componentSettings.scaleDuration,
          ease: "power1.inOut",
          delay: totalCycle() * 1.5,
        })
        .to(
          identifier,
          {
            opacity: 1,
            ease: "power1.inOut",
            duration: componentSettings.opacityDuration,
            onStart: () => {
              if (!isMobile) {
                player.play()
              }
            },
            onComplete: () => props.handleHeroLoad(),
          },
          "<"
        )
    }
  }

  const animateVideoPIP = (identifier) => {
    gsap.to(identifier, {
      scale: 0.5,
      top: "30%",
      left: "85%",
      duration: 3,
    })
  }

  const animateVideoBackCenter = (identifier) => {
    gsap.to(identifier, {
      scale: 1,
      duration: 3,
      left: "50%",
      top: "50%",
    })
  }

  /**
   * Setup player and animate it
   * @param {object} success Success object generated by Brightcove on successful video load
   */
  const onSuccess = (success) => {
    const player = success.ref
    props.setPlayer(player)
    console.log(player)
    player.on("loadedmetadata", () => {
      // video has finished fetching
      console.log("video loaded")
      setupPlayer(player)

      // window.addEventListener("visibilitychange", () => {
      //   if(window.hidden){
      //     gsap.ticker.lagSmoothing(false)
      //   } else {
      //     gsap.ticker.lagSmoothing(500,33)
      //   }
      // })

      // window.addEventListener('blur', () => {
      //   player.pause()
      //   gsap.globalTimeline.pause()
      //   }, false);
      //   window.addEventListener('focus', () => {
      //     player.play()
      //     gsap.globalTimeline.play()
      //   }, false);

      animateVideo(
        props.heroTl,
        ".hero-video__container",
        sampleData.years[years[props.currentSegment]].heroVideo,
        player.mediainfo.duration,
        player,
        props.bookend
      )
    })
  }

  /**
   * Parse link provided by Brightcove to use in player component
   * @param {string} bcLink Link provided by BMC team
   * @return {array} Array of link portions: accountId, videoId, playerId, embedId
   */
  const parseBCLink = (bcLink) => {
    let splitLink = bcLink.split("/")
    return [
      splitLink[3],
      splitLink[5].split("=")[1],
      splitLink[4].split("_")[0],
      splitLink[4].split("_")[1],
    ]
  }

  const [accountId, videoId, playerId, embedId] = parseBCLink(
    sampleData.years[years[props.currentSegment]].heroVideo.link
  )

  /**
   * Create the player and its supporting content/subcomponents
   * @return {Element} Player and subcomponents
   */
  const generatePlayer = () => {
    return (
      <div className='hero-video__wrapper'>
        <div ref={heroRef} className='hero-video__container'>
          <div className='hero-video__player'>
            <ReactPlayerLoader
              key={`${accountId}-${videoId}-${playerId}-${embedId}`}
              accountId={accountId}
              videoId={videoId}
              playerId={playerId}
              embedId={embedId}
              onSuccess={onSuccess}
            />
          </div>
        </div>
        {generateSubcomponents(
          sampleData.years[years[props.currentSegment]].cards
        )}
      </div>
    )
  }

  if (redirectSearch) {
    return <Redirect to={"/search"} />
  }

  return generatePlayer()
}

HeroVideo.propTypes = {
  currentSegment: PropTypes.number.isRequired,
  handleHeroComplete: PropTypes.func,
  handleHeroLoad: PropTypes.func,
  heroTl: PropTypes.object.isRequired,
  cleaningUp: PropTypes.bool,
}

export default HeroVideo
